import React from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { LocationData } from '../../services/DashboardService';
import { formatCurrency, formatPercent } from '../../utils/formatters';

const { Text } = Typography;

interface AverageCardProps {
  locations: LocationData[];
  viewMode: 'weekly' | 'quarterly';
}

export const AverageCard: React.FC<AverageCardProps> = ({ locations, viewMode }) => {
  const avgToast = locations.reduce((sum, loc) => sum + loc.weeklySalesToast, 0) / locations.length;
  const avgCatering = locations.reduce((sum, loc) => sum + (loc.weeklyCatering ?? 0), 0) / locations.length;
  const avgTotal = locations.reduce((sum, loc) => sum + loc.weeklyTotal, 0) / locations.length;
  const avgGfs = locations.reduce((sum, loc) => sum + (loc.weeklyGfs ?? 0), 0) / locations.length;
  const avgGfsPercent =
    locations.length > 0
      ? locations.reduce((sum, loc) => sum + loc.gfsPercent, 0) / locations.length
      : 0;
  const avgVariance = locations.reduce((sum, loc) => sum + loc.varianceFromAvg, 0) / locations.length;
  const varianceColor = avgVariance <= -2 ? '#52c41a' : avgVariance >= 2 ? '#ff4d4f' : undefined;
  const avg8WeekGfs = locations.reduce((sum, loc) => sum + (loc.eightWeekTotalGfs ?? 0), 0) / locations.length;
  const avg8WeekSales = locations.reduce((sum, loc) => sum + loc.eightWeekTotalSales, 0) / locations.length;
  const avg8WeekPercent = avg8WeekSales > 0 ? (avg8WeekGfs / avg8WeekSales) * 100 : null;

  return (
    <Card size="small" style={{ marginTop: 8, backgroundColor: '#f5f5f5', border: '2px solid #d9d9d9' }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 16, display: 'block' }}>
          Average
        </Text>
      </div>
      <Row gutter={[8, 8]}>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Toast
          </Text>
          <div style={{ fontWeight: 500 }}>{formatCurrency(avgToast)}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Catering
          </Text>
          <div style={{ fontWeight: 500 }}>{formatCurrency(avgCatering)}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly Total
          </Text>
          <div style={{ fontWeight: 500 }}>{formatCurrency(avgTotal)}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Weekly GFS
          </Text>
          <div style={{ fontWeight: 500 }}>
            {avgGfs > 0 ? formatCurrency(avgGfs) : <span style={{ color: '#d9d9d9' }}>--</span>}
          </div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            GFS %
          </Text>
          <div style={{ fontWeight: 500 }}>{formatPercent(avgGfsPercent)}</div>
        </Col>
        <Col span={12}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Variance from Avg
          </Text>
          <div style={{ fontWeight: 500, color: varianceColor }}>{formatPercent(Math.abs(avgVariance))}</div>
        </Col>
        {viewMode === 'quarterly' ? (
          <>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Total GFS
              </Text>
              <div style={{ fontWeight: 500 }}>
                {avg8WeekGfs > 0 ? formatCurrency(avg8WeekGfs) : <span style={{ color: '#d9d9d9' }}>--</span>}
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Total Sales
              </Text>
              <div style={{ fontWeight: 500 }}>
                {avg8WeekSales > 0 ? formatCurrency(avg8WeekSales) : <span style={{ color: '#ff4d4f' }}>--</span>}
              </div>
            </Col>
          </>
        ) : (
          <>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                8th Week Sales
              </Text>
              <div style={{ fontWeight: 500 }}>
                {avg8WeekSales > 0 ? formatCurrency(avg8WeekSales) : <span style={{ color: '#ff4d4f' }}>--</span>}
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                8th Week GFS
              </Text>
              <div style={{ fontWeight: 500 }}>
                {avg8WeekGfs > 0 ? formatCurrency(avg8WeekGfs) : <span style={{ color: '#d9d9d9' }}>--</span>}
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                8th Week GFS %
              </Text>
              <div style={{ fontWeight: 500 }}>
                {avg8WeekPercent !== null ? formatPercent(avg8WeekPercent) : '--'}
              </div>
            </Col>
          </>
        )}
      </Row>
    </Card>
  );
};

